import { useCallback, useEffect, useRef } from 'react'
import * as yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { socket, SocketEvent } from '../sockets'
import { counts, CountsPayload } from '../sockets/counts'
import { chatConversationsQueryKey } from '../api/chat'
import { MessageRow } from '../generated/api/chat-room'
import {
  BinderUpdatedPayload,
  BookingUpdatedPayload,
  ChatRoomUpdatedPayload,
  CommentUpdatedPayload,
  ContentUpdatedPayload,
  EventUpdatedPayload,
  PostUpdatedPayload,
  RegisterUpdatedPayload,
  UserUpdatedPayload,
} from '../generated/api/live-events'
import {
  applyRoomUpdated,
  insertMessageIntoCache,
  invalidateRoomReads,
} from '../components/chat/Room/useRoomMessagesCache'
import { infiniteTicketQueries, ticketQueries } from '../api/ticket'
import { useAuth } from './useAuth'
import { notificationsQueries } from '../api/notifications'
import { featureQueries } from '../api/features'
import {
  applyMembershipUpdated,
  applyRegisterUpdated,
  applySocietySettingsUpdated,
} from '../api/societies'
import {
  applyEventUpdated,
  applyPostUpdated,
  invalidatePublishedPost,
} from '../components/feed/postRowCache'
import { applyContentUpdated } from '../components/feed/contentCache'
import { applyCommentUpdated } from '../components/feed/commentCache'
import { applyBookingUpdated } from '../api/bookings'
import { applyBinderUpdated } from '../api/binder'
import { applyUserUpdated } from '../api/users'

type TicketSocketAction =
  | 'new-ticket'
  | 'new-ticket-from-board'
  | 'new-ticket-message'
  | 'new-ticket-message-from-board'
  | 'ticket-status'
  | 'ticket-assigned'
  | 'ticket-variant'

const validTicketActions: TicketSocketAction[] = [
  'new-ticket',
  'new-ticket-from-board',
  'new-ticket-message',
  'new-ticket-message-from-board',
  'ticket-status',
  'ticket-assigned',
  'ticket-variant',
]

export const ticketPayloadSchema = yup.object({
  societyId: yup.string().required(),
  ticketId: yup.string().optional(),
  action: yup.mixed().oneOf(validTicketActions),
})

const getTicketUpdatedPayload = (
  payload: unknown
): yup.InferType<typeof ticketPayloadSchema> | false => {
  try {
    const ticketPayload = ticketPayloadSchema.validateSync(payload)
    return ticketPayload
  } catch (err) {
    return false
  }
}

const parsePayload = <T>(payload: T | string): T =>
  typeof payload === 'string' ? (JSON.parse(payload) as T) : payload

const useWebsocket = (): void => {
  const client = useQueryClient()
  const { token, userId, logout } = useAuth()
  // logout changes identity with the auth state; the socket must not reconnect.
  const logoutRef = useRef(logout)
  logoutRef.current = logout

  const setWebsocket = useCallback(
    async (_token: string): Promise<void> => {
      // Initiate socket
      await socket.setSocket(_token, [
        {
          event: SocketEvent.CONNECT,
          func: (): void => {
            counts.connected(client)
          },
        },
        {
          event: SocketEvent.DISCONNECT,
          func: (): void => {
            counts.disconnected()
          },
        },
        {
          event: SocketEvent.MESSAGE_CREATED,
          func: (payload: MessageRow | string): void => {
            const row = parsePayload(payload)
            insertMessageIntoCache(client, row)
            invalidateRoomReads(client, row.roomId)
            client.invalidateQueries({ queryKey: chatConversationsQueryKey })
          },
        },
        {
          event: SocketEvent.RECEIPT_MOVED,
          func: (payload: { roomId: string } | string): void => {
            const { roomId } = parsePayload(payload)
            invalidateRoomReads(client, roomId)
          },
        },
        {
          event: SocketEvent.ROOM_UPDATED,
          func: (payload: ChatRoomUpdatedPayload | string): void => {
            applyRoomUpdated(client, parsePayload(payload))
          },
        },
        {
          event: SocketEvent.ROOMS_NEW,
          func: (): void => {
            client.invalidateQueries({ queryKey: chatConversationsQueryKey })
            client.invalidateQueries({
              queryKey:
                notificationsQueries.getUserUnseenUnreadCount(userId).queryKey,
            })
          },
        },
        {
          event: SocketEvent.NOTIFICATION_COUNTS,
          func: (payload: CountsPayload | string): void => {
            counts.arrived(client, parsePayload(payload), userId)
          },
        },
        {
          event: SocketEvent.FEATURES_UPDATED,
          func: (): void => {
            client.invalidateQueries({
              queryKey: featureQueries.getFeatures().queryKey,
            })
          },
        },
        {
          event: SocketEvent.SOCIETY_SETTINGS_UPDATED,
          func: (payload: { societyId: string } | string): void => {
            applySocietySettingsUpdated(client, parsePayload(payload), userId)
          },
        },
        {
          event: SocketEvent.POST_PUBLISHED,
          func: (
            payload: { societyId: string; postId: string } | string
          ): void => {
            const { postId } = parsePayload(payload)
            invalidatePublishedPost(client, postId)
          },
        },
        {
          event: SocketEvent.POST_UPDATED,
          func: (payload: PostUpdatedPayload | string): void => {
            applyPostUpdated(client, parsePayload(payload))
          },
        },
        {
          event: SocketEvent.COMMENT_UPDATED,
          func: (payload: CommentUpdatedPayload | string): void => {
            applyCommentUpdated(client, parsePayload(payload))
          },
        },
        {
          event: SocketEvent.EVENT_UPDATED,
          func: (payload: EventUpdatedPayload | string): void => {
            applyEventUpdated(client, parsePayload(payload), userId)
          },
        },
        {
          event: SocketEvent.BOOKING_UPDATED,
          func: (payload: BookingUpdatedPayload | string): void => {
            applyBookingUpdated(client, parsePayload(payload))
          },
        },
        {
          event: SocketEvent.CONTENT_UPDATED,
          func: (payload: ContentUpdatedPayload | string): void => {
            applyContentUpdated(client, parsePayload(payload))
          },
        },
        {
          event: SocketEvent.REGISTER_UPDATED,
          func: (payload: RegisterUpdatedPayload | string): void => {
            applyRegisterUpdated(client, parsePayload(payload), userId)
          },
        },
        {
          event: SocketEvent.MEMBERSHIP_UPDATED,
          func: (): void => {
            applyMembershipUpdated(client)
          },
        },
        {
          event: SocketEvent.BINDER_UPDATED,
          func: (payload: BinderUpdatedPayload | string): void => {
            applyBinderUpdated(client, parsePayload(payload))
          },
        },
        {
          event: SocketEvent.USER_UPDATED,
          func: (payload: UserUpdatedPayload | string): void => {
            applyUserUpdated(client, parsePayload(payload), userId, () =>
              logoutRef.current()
            )
          },
        },
        {
          event: SocketEvent.TICKET_UPDATED,
          func: (ticketPayload: unknown): void => {
            const parsedTicketPayload =
              typeof ticketPayload === 'string'
                ? JSON.parse(ticketPayload)
                : ticketPayload
            const payload = getTicketUpdatedPayload(parsedTicketPayload)

            if (!payload) {
              return
            }

            const { societyId, ticketId } = payload

            client.invalidateQueries({
              queryKey: infiniteTicketQueries.tickets({ societyId }).queryKey,
            })
            client.invalidateQueries({
              queryKey: infiniteTicketQueries.ticketsBoard({ societyId })
                .queryKey,
            })
            client.invalidateQueries({
              queryKey: [{ scope: 'ticket', entity: 'getUnreadTicketsCount' }],
            })

            if (ticketId) {
              client.invalidateQueries({
                queryKey: ticketQueries.getTicket(ticketId, societyId).queryKey,
              })
              client.invalidateQueries({
                queryKey: ticketQueries.getTicketAsBoard(ticketId, societyId)
                  .queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMessages({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMedia({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMessagesAsBoard({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMediaBoard({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketDocuments({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketDocumentsBoard({
                  societyId,
                  ticketId,
                }).queryKey,
              })
            }
          },
        },
      ])
    },
    [client, userId]
  )

  useEffect(() => {
    if (token) {
      setWebsocket(token)
    }
  }, [token, setWebsocket])
}

useWebsocket.displayName = 'useWebsocket'
export { useWebsocket }
